import { Employee, BudgetData } from '../contexts/AppContext';
import { calculateTeamSalaryTotal } from '../utils/payrollCalculations.ts';
import { saveBudgetMonthReplace, getBudgetData } from './firebase/budgetFirebaseService.ts';
import { getTeamAdjustmentCarryover } from '../utils/payrollCalculations.ts';

/**
 * 팀별 목표매출 계산 결과 타입
 */
export interface TeamTargetSalesResult {
  teamTargetSales: Record<string, number>;      // 최종 목표매출 (= 순수 + 이월금액)
  teamAdjustmentCarryover: Record<string, number>; // 팀별 이월금액 — 목표와 함께 저장돼야 한다
  teamPureTargetSales: Record<string, number>;  // 순수 목표매출 (인건비율 기반)
  teamSalaryTotals: Record<string, number>;     // 팀별 인건비 총액
  totalTargetSales: number;                     // 전체 목표매출 합계
}

/**
 * 계산 옵션
 */
export interface CalculationOptions {
  includePreviousMonthAdjustment?: boolean;  // 지난달 미달성 조정 포함 여부 (해당 월 applyPreviousMonthAdjustment 토글과 AND 게이트)
  verbose?: boolean;                         // 상세 로그 출력 여부
}

/**
 * 모든 팀의 목표매출을 계산합니다
 * (급여 관리의 recalculateTeamTargetSales 로직을 순수 함수로 추출)
 *
 * @param yearMonth 계산할 년월 (YYYY-MM 형식)
 * @param employees 직원 데이터 배열
 * @param budgetData 예산 데이터
 * @param options 계산 옵션
 * @returns 팀별 목표매출 계산 결과
 */
export async function calculateAllTeamsTargetSales(
  yearMonth: string,
  employees: Employee[],
  budgetData: BudgetData,
  options: CalculationOptions = {}
): Promise<TeamTargetSalesResult> {
  const { includePreviousMonthAdjustment = true, verbose = false } = options;

  if (verbose) {
    console.log(`[TeamTargetSalesService] ${yearMonth} 모든 팀 목표매출 계산 시작`);
  }

  // 1. 팀 우주 = 이 달의 팀 목록 ∪ 직원이 속한 팀
  //    직원만 보면 잠시 0명이 된 팀의 이월·목표 키가 통째 교체에서 빠져 유실된다 (Codex 재심 P0-4).
  const monthTeams: string[] = Array.isArray(budgetData.monthlyData?.[yearMonth]?.teams)
    ? budgetData.monthlyData[yearMonth].teams
    : [];
  const actualTeams = [...new Set(
    [...monthTeams, ...employees.map(emp => emp.team)]
      .filter(team => team && String(team).trim() !== '')
  )].sort();

  if (verbose) {
    console.log(`[TeamTargetSalesService] 계산 대상 팀: ${actualTeams.length}개`, actualTeams);
  }

  // 2. 지난달 데이터 가져오기 (미달성 조정용)
  const previousMonth = getPreviousYearMonth(yearMonth);
  const previousMonthData = budgetData.monthlyData?.[previousMonth];

  // 🔒 이 서비스는 조정을 '적용'하지 않는다 — 저장된 이월금액을 읽어 더할 뿐이다.
  //    이전에는 토글이 켜져 있으면 여기서도 순수+지난달미달성을 계산해 저장했는데,
  //    이월금액은 기록하지 않아 "이월 포함 목표 + 이월 0"이 영속됐다(불변식 위반 A).
  //    적용은 급여 화면의 명시적 행동(일괄 적용·재계산) 소관이다.
  //    이월은 호출자가 넘긴 메모리 스냅샷이 아니라 DB에서 방금 읽은 값을 쓴다.
  //    성과 새로고침 탭이 오래된 스냅샷을 들고 있으면, 다른 탭이 방금 적용·저장한 이월을
  //    0으로 덮어 지운다 (Codex 재심 P0-5). 읽기 실패 시에만 스냅샷으로 폴백한다.
  let carryoverSource: any = budgetData.monthlyData?.[yearMonth];
  try {
    const fresh = await getBudgetData();
    if (fresh?.monthlyData?.[yearMonth]) carryoverSource = fresh.monthlyData[yearMonth];
  } catch (e) {
    console.warn(`[TeamTargetSalesService] ${yearMonth} 이월 재조회 실패 — 메모리 스냅샷 사용`, e);
  }
  const currentMonthData = carryoverSource;
  void includePreviousMonthAdjustment; // @deprecated — 판정에 쓰지 않는다 (적용은 급여 화면의 명시적 행동)
  void previousMonthData;

  // 3. 결과 객체 초기화
  const allTeamsPureTargetSales: Record<string, number> = {};
  const allTeamsSalaryTotals: Record<string, number> = {};
  const allTeamsTargetSales: Record<string, number> = {};
  const allTeamsCarryover: Record<string, number> = {};

  // 4. 각 팀별로 목표매출 계산
  actualTeams.forEach(teamName => {
    // 팀 직원 필터링
    const teamEmployees = employees.filter(emp => emp.team === teamName);

    // 팀 인건비 총액 계산 (실장 인건비 배분 포함)
    // 급여관리 recalculateTeamTargetSales와 동일한 정의: 팀 인건비 + 실장 배분.
    // 두 경로가 같은 monthlyData.teamTargetSales에 다른 값을 써 last-writer-wins
    // 비결정성이 발생하던 문제를 제거한다.
    // 🔒 실장 배분 정본: calculateTeamSalaryTotal이 실장을 1회만 계상 (본인 팀 이중계상 방지, 급여관리 경로와 동일 정의)
    const teamSalaryTotal = calculateTeamSalaryTotal(employees, teamName, employees);

    // 팀별 인건비율 가져오기
    const teamPersonnelPercentage = getEffectivePersonnelPercentage(
      teamName,
      budgetData,
      yearMonth
    );

    // 순수 목표매출 계산 (인건비 총액 ÷ 인건비율)
    const pureTargetSales = teamPersonnelPercentage > 0
      ? Math.round(teamSalaryTotal / (teamPersonnelPercentage / 100))
      : 0;

    // 결과 저장
    allTeamsPureTargetSales[teamName] = pureTargetSales;
    allTeamsSalaryTotals[teamName] = teamSalaryTotal;

    // 최종 목표매출 = 순수 + 저장된 이월금액 (정본)
    const carryover = getTeamAdjustmentCarryover(currentMonthData, teamName);
    const finalTargetSales = pureTargetSales + carryover;
    allTeamsCarryover[teamName] = carryover;

    allTeamsTargetSales[teamName] = finalTargetSales;

    if (verbose) {
      console.log(`[TeamTargetSalesService] ${teamName} 계산 완료:`, {
        직원수: teamEmployees.length,
        인건비총액: teamSalaryTotal.toLocaleString() + '원',
        인건비율: teamPersonnelPercentage + '%',
        순수목표매출: pureTargetSales.toLocaleString() + '원',
        최종목표매출: finalTargetSales.toLocaleString() + '원'
      });
    }
  });

  // 5. 전체 목표매출 합계 계산
  const totalTargetSales = Object.values(allTeamsTargetSales).reduce(
    (sum, val) => sum + (Number(val) || 0),
    0
  );

  if (verbose) {
    console.log(`[TeamTargetSalesService] ${yearMonth} 계산 완료:`, {
      팀수: actualTeams.length,
      총순수목표매출: Object.values(allTeamsPureTargetSales).reduce((s, v) => s + v, 0).toLocaleString(),
      총최종목표매출: totalTargetSales.toLocaleString()
    });
  }

  return {
    teamTargetSales: allTeamsTargetSales,
    teamAdjustmentCarryover: allTeamsCarryover,
    teamPureTargetSales: allTeamsPureTargetSales,
    teamSalaryTotals: allTeamsSalaryTotals,
    totalTargetSales
  };
}

// (제거됨) 로컬 calculateSalaryTotal — 정본 calculateTeamSalaryTotal로 통일 후 무호출.
// 팀 인건비 총액이 필요하면 utils/payrollCalculations의 calculateTeamSalaryTotal(실장 1회 계상)을 쓸 것.

/**
 * 팀별 유효 인건비율을 가져옵니다
 * (급여 관리의 getEffectivePersonnelPercentage 로직)
 *
 * 우선순위:
 * 1. 월별 팀 인건비율 (monthlyData[yearMonth].teamPersonnelPercentages[teamName])
 * 2. 전역 팀 인건비율 (budgetData.teamPersonnelPercentages[teamName])
 * 3. 월별 전역 인건비율 (monthlyData[yearMonth].personnelPercentage)
 * 4. 전역 인건비율 (budgetData.personnelPercentage)
 * 5. 기본값: 25%
 *
 * @param teamName 팀 이름
 * @param budgetData 예산 데이터
 * @param yearMonth 년월 (옵션)
 * @returns 인건비율 (%)
 */
export function getEffectivePersonnelPercentage(
  teamName: string,
  budgetData: BudgetData,
  yearMonth?: string
): number {
  // 1순위: 월별 팀 인건비율
  if (yearMonth) {
    const monthlyTeamPercentage = budgetData.monthlyData?.[yearMonth]?.teamPersonnelPercentages?.[teamName];
    if (monthlyTeamPercentage !== undefined && monthlyTeamPercentage > 0) {
      return monthlyTeamPercentage;
    }
  }

  // 2순위: 전역 팀 인건비율
  const globalTeamPercentage = budgetData.teamPersonnelPercentages?.[teamName];
  if (globalTeamPercentage !== undefined && globalTeamPercentage > 0) {
    return globalTeamPercentage;
  }

  // 3순위: 월별 전역 인건비율
  if (yearMonth) {
    const monthlyPercentage = budgetData.monthlyData?.[yearMonth]?.personnelPercentage;
    if (monthlyPercentage !== undefined && monthlyPercentage > 0) {
      return monthlyPercentage;
    }
  }

  // 4순위: 전역 인건비율
  if (budgetData.personnelPercentage !== undefined && budgetData.personnelPercentage > 0) {
    return budgetData.personnelPercentage;
  }

  // 기본값
  return 25;
}

/**
 * 지난달 미달성액을 계산합니다
 * (급여 관리의 calculatePreviousMonthUnderperformance 로직)
 *
 * @param teamName 팀 이름
 * @param previousMonthData 지난달 월별 데이터
 * @returns 지난달 미달성액
 */
export function calculatePreviousMonthUnderperformance(
  teamName: string,
  previousMonthData: any
): number {
  const teamPerformance = previousMonthData?.teamPerformanceData?.[teamName];

  if (!teamPerformance) {
    return 0;
  }

  const underperformanceSales = teamPerformance.underperformanceSales || 0;

  // 미달성액이 양수인 경우만 반환 (음수 방지)
  return Math.max(0, underperformanceSales);
}

/**
 * 팀과 팀장이 매칭되는지 확인합니다
 * (급여 관리의 checkTeamAndLeaderMatch 로직)
 *
 * @param teamName 팀 이름
 * @param leaderName 팀장 이름
 * @param previousMonthData 지난달 월별 데이터
 * @returns 매칭 여부
 */
export function checkTeamAndLeaderMatch(
  teamName: string,
  leaderName: string,
  previousMonthData: any
): boolean {
  if (!leaderName || !previousMonthData?.employees) {
    return false;
  }

  // 지난달 직원 데이터에서 해당 팀장 찾기
  const previousEmployees = previousMonthData.employees;
  const previousLeader = previousEmployees.find(
    (emp: Employee) => emp.name === leaderName && emp.team === teamName && emp.position === '팀장'
  );

  return !!previousLeader;
}

/**
 * 이전 월 (YYYY-MM) 문자열을 반환합니다
 *
 * @param yearMonth 현재 년월 (YYYY-MM)
 * @returns 이전 년월 (YYYY-MM)
 */
export function getPreviousYearMonth(yearMonth: string): string {
  const [year, month] = yearMonth.split('-').map(Number);
  const date = new Date(year, month - 1, 1); // month는 0-based
  date.setMonth(date.getMonth() - 1);

  const previousYear = date.getFullYear();
  const previousMonth = (date.getMonth() + 1).toString().padStart(2, '0');

  return `${previousYear}-${previousMonth}`;
}

/**
 * 계산 결과를 monthlyData에 저장합니다
 *
 * @param yearMonth 년월
 * @param result 계산 결과
 */
export async function saveTeamTargetSalesToMonthlyData(
  yearMonth: string,
  result: TeamTargetSalesResult
): Promise<void> {
  console.log(`[TeamTargetSalesService] ${yearMonth} monthlyData 저장 시작`);

  // per-month 저장: 해당 월의 목표매출 필드만 갱신(다른 달 무손상).
  // 팀맵(teamTargetSales/teamPureTargetSales/teamSalaryTotals)은 통째 교체로 옛 팀 키 제거.
  await saveBudgetMonthReplace(
    yearMonth,
    {
      targetSales: result.totalTargetSales,
      calculatedAt: new Date().toISOString(),
      calculatedBy: 'TeamTargetSalesService'
    },
    {
      teamTargetSales: result.teamTargetSales,
      teamAdjustmentCarryover: result.teamAdjustmentCarryover, // 🔒 목표와 같은 저장에
      teamPureTargetSales: result.teamPureTargetSales,
      teamSalaryTotals: result.teamSalaryTotals
    }
  );

  console.log(`[TeamTargetSalesService] ${yearMonth} monthlyData 저장 완료:`, {
    팀수: Object.keys(result.teamTargetSales).length,
    총목표매출: result.totalTargetSales.toLocaleString() + '원'
  });
}
